import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from "react";
import { api, ApiError, listerSessionLabelPath, type ListerEnrichRow, type ListerLiveProgress, type ListerSchema, type ListerSession, type ListerVocab } from "../../lib/api";
import { printDocument } from "../../lib/print";
import { reducer, merged, clientValidate, sessionLabel, buyTradeCertMismatch, buyTradeConditionFlag, BUY_TRADE_FIXED_NOTE, type Action, type FormState } from "../../lib/lister-state";
import type { ListerCardCondition } from "../../lib/card-condition";
import type { ListerAutographType } from "../../lib/card-autograph";
import { sanitizeHtml } from "../../lib/sanitize";
import { fmtCents } from "../../lib/format";
import StepProgress, { MAP_STAGE_LABEL } from "./components/StepProgress";
import VerdictBanner from "./components/VerdictBanner";
import BuyTradeBadge from "./components/BuyTradeBadge";
import ImagePicker from "./components/ImagePicker";
import AddPhotos from "./components/AddPhotos";
import CategoryPicker from "./components/CategoryPicker";
import AttributeFields from "./components/AttributeFields";
import TagEditor from "./components/TagEditor";
import CollectionsEditor from "./components/CollectionsEditor";
import PriceCard from "./components/PriceCard";
import NumberField from "./components/NumberField";
import EvidenceDrawer from "./components/EvidenceDrawer";
import ActionBar from "./components/ActionBar";
import CardPanel from "./components/CardPanel";
import EnrichPanel from "./components/EnrichPanel";
import { hashFor } from "../../lib/routes";

// "identified" is a real, if usually brief, status a CARD session passes through between the
// CERT stage and RESEARCH (src/lister/card/stages.ts sets it; src/lister/pipeline.ts moves the
// session on to "researching" a few statements later, in the same run). Leaving it out of this
// set is a genuine race: a poll landing in that window sees a non-transitional status, calls
// load() exactly once against a session with no proposal yet, and then — because the polling
// loop only re-arms itself inside the TRANSITIONAL branch — never polls again. The operator is
// left on a permanently frozen "Researching… this page updates itself." for a pipeline that
// went on to finish seconds later.
const TRANSITIONAL = new Set(["researching", "researched", "identified", "proposing", "posting"]);
const KNOWN_409: Record<string, string> = {
  post_in_progress: "A create is already running for this session.",
  session_busy: "Wait for the current research or remap to finish.",
  already_posted: "Already created — open it in Shopify.",
  attention_required: "Confirm the red verdict first.",
};
const Section = ({ title, children }: { title: string; children: ReactNode }) => (<section className="bg-zinc-900 border border-zinc-800 rounded-md p-3 space-y-2"><h2 className="text-zinc-400 text-xs uppercase tracking-wide">{title}</h2>{children}</section>);
const Field = ({ label, children }: { label: string; children: ReactNode }) => (<label className="block"><span className="block text-xs text-zinc-400 mb-1">{label}</span>{children}</label>);
const inputCls = "w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 font-mono text-sm focus:outline-none focus:border-zinc-600 disabled:opacity-50";
const imagesOf = (session: ListerSession | null): string[] => session?.research?.images.filter((i) => i.served_url).map((i) => i.url) ?? [];

const STAGE_LABEL: Record<string, string> = { read: "Reading the card", cert: "Checking the cert", research: "Researching", images: "Images", identify: "Identifying", refute: "Cross-checking", price: "Pricing in grade", map: MAP_STAGE_LABEL, done: "Done", failed: "Failed" };

/** Live feedback while the pipeline runs: the server's last note, how long ago it was written, and a stall warning.
 *  The poll runs every 1.5 s and the server re-stamps the note every 10 s, so "updated Ns ago" climbing past ~45 s
 *  means the server process is not making progress. */
function LiveLine({ live }: { live: ListerLiveProgress | null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(t); }, []);
  if (!live) return <p className="text-xs text-zinc-500" role="status">Waiting for the first update from the server…</p>;
  const ago = Math.max(0, Math.round((now - Date.parse(live.at)) / 1000));
  const stageSecs = Math.max(0, Math.round((now - Date.parse(live.started_at)) / 1000));
  const stalled = ago >= 120; const slow = ago >= 45;
  return (
    <div className={`text-xs rounded border p-2 space-y-1 ${stalled ? "border-red-700/60 bg-red-900/20 text-red-200" : slow ? "border-yellow-700/60 bg-yellow-900/20 text-yellow-200" : "border-zinc-800 bg-zinc-950 text-zinc-300"}`} role="status" aria-live="polite">
      <div><span className="text-zinc-400">{STAGE_LABEL[live.stage] ?? live.stage}</span> — {live.note}</div>
      <div className="text-zinc-500">updated {ago}s ago · this stage {stageSecs}s</div>
      {stalled && <div>No update for {ago}s — the server looks stuck. Press retry or check the server log.</div>}
      {!stalled && slow && <div>No update for {ago}s — Claude calls can take a few minutes; a fresh note should arrive shortly.</div>}
    </div>
  );
}

export default function Review({ sessionId }: { sessionId: string }) {
  const [session, setSession] = useState<ListerSession | null>(null);
  const [schema, setSchema] = useState<ListerSchema | null>(null);
  const [vocab, setVocab] = useState<ListerVocab | null>(null);
  const [form, dispatch] = useReducer(reducer, null as unknown as FormState);
  const [confirmed, setConfirmed] = useState(false);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState<{ adminUrl: string; warnings: string[] } | null>(null);
  const [printingLabel, setPrintingLabel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState<ListerLiveProgress | null>(null);
  const [serverIssues, setServerIssues] = useState<Array<{ field: string; message: string }>>([]);
  const [conditionSaving, setConditionSaving] = useState(false);
  const [autographSaving, setAutographSaving] = useState(false);
  // The "Already in Shopify" action row (Task 11): this is CardPanel's cert duplicate given a
  // real control for the first time, and the barcode flow's own second chance when the
  // operator pressed "Create anyway" past a scan-time duplicate instead of restocking. Local,
  // not server state — dismissing it (Create anyway) never touches the session; it only lets
  // the normal form render so the real Create button in ActionBar can do that.
  const [dismissedExisting, setDismissedExisting] = useState(false);
  const [existingAction, setExistingAction] = useState<"none" | "restock" | "enrich">("none");
  const [restockN, setRestockN] = useState(1);
  const [restockMode, setRestockMode] = useState<"add" | "replace">("add");
  const [restockArmed, setRestockArmed] = useState(false);
  const [restockBusy, setRestockBusy] = useState(false);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [enrichRows, setEnrichRows] = useState<ListerEnrichRow[]>([]);
  const [enrichApply, setEnrichApply] = useState<Record<string, boolean>>({});
  const [enrichAppendImages, setEnrichAppendImages] = useState(false);
  const [enrichBusy, setEnrichBusy] = useState(false);
  const [enrichResult, setEnrichResult] = useState<{ applied: string[]; warnings: string[]; admin_url: string } | null>(null);
  const [enrichFailure, setEnrichFailure] = useState<{ step: string; applied: string[] } | null>(null);
  const initializedFor = useRef<string | null>(null);
  const saveTimer = useRef<number | null>(null);
  const pollGen = useRef(0);
  const mountedRef = useRef(true);
  const formRef = useRef<FormState | null>(null);
  formRef.current = form;
  const sessionRef = useRef<ListerSession | null>(null);
  sessionRef.current = session;

  // every user-driven edit goes through here (never the "init" load) so a
  // stale 422's field highlights clear the moment the operator changes anything.
  const change = useCallback((action: Action) => {
    dispatch(action);
    setServerIssues([]);
  }, []);

  const handleApiError = useCallback((e: unknown) => {
    if (e instanceof ApiError && e.status === 409) {
      const body = e.body as { error?: string; message?: string };
      const mapped = body.error ? KNOWN_409[body.error] : undefined;
      setError(mapped ?? body.message ?? String(e));
    } else if (e instanceof ApiError && e.status === 422) {
      const body = e.body as { issues?: Array<{ field: string; message: string }> };
      setServerIssues(body.issues ?? []);
    } else {
      setError(String(e));
    }
  }, []);

  const load = useCallback(async (): Promise<ListerSession> => {
    const s = await api.getListerSession(sessionId);
    setSession(s);
    const key = `${s.id}:${s.updated_at}:${s.status}`;
    if (s.proposal && initializedFor.current !== key) {
      initializedFor.current = key;
      dispatch({ type: "init", proposal: s.proposal, edits: s.edits, images: imagesOf(s) });
      setSchema(s.schema);
    } else if (!s.proposal) {
      // research was reset (e.g. a forced retry): forget the stale key so the
      // next proposal re-initialises the form instead of being
      // skipped by the key check below.
      initializedFor.current = null;
    }
    const adminUrl = s.admin_url;
    if (s.status === "posted" && adminUrl) setPosted((p) => p ?? { adminUrl, warnings: [] });
    return s;
  }, [sessionId]);

  // light poll loop: getListerStatus every 1.5s while the session is transitional,
  // then exactly one getListerSession once it settles — never getListerSession in the loop.
  const startPolling = useCallback(() => {
    const gen = ++pollGen.current;
    const tick = async () => {
      if (pollGen.current !== gen) return;
      try {
        const s = await api.getListerStatus(sessionId);
        if (pollGen.current !== gen) return;
        setSession((prev) => (prev ? { ...prev, status: s.status, step_log: s.step_log, verdict: s.verdict, error: s.error, updated_at: s.updated_at } : prev));
        setLive(s.progress ?? null);
        if (TRANSITIONAL.has(s.status)) {
          window.setTimeout(() => { void tick(); }, 1500);
        } else {
          await load();
        }
      } catch (e) {
        if (pollGen.current === gen) setError(String(e));
      }
    };
    void tick();
  }, [sessionId, load]);

  useEffect(() => {
    mountedRef.current = true; // re-arm on every (re)mount — StrictMode's dev double-invoke would otherwise leave this false forever
    let alive = true;
    (async () => {
      try {
        const s = await load();
        if (alive && TRANSITIONAL.has(s.status)) startPolling();
      } catch (e) {
        if (alive) setError(String(e));
      }
    })();
    api.getListerVocab().then((v) => { if (mountedRef.current) setVocab(v); }).catch(() => {});
    return () => { alive = false; mountedRef.current = false; pollGen.current += 1; };
  }, [load, startPolling]);

  const m = form ? merged(form, { mode: session?.mode ?? "barcode", buy_trade: session?.buy_trade ?? null }) : null;
  const available = useMemo(() => imagesOf(session), [session]);
  const issues = useMemo(() => (m ? clientValidate(m, schema, available, {
    mode: session?.mode ?? "barcode",
    // is_raw is the reconciled CertEnvelope.facts.is_slab, never row.mode alone — a graded
    // card is a card-mode session too, and a barcode session's cert is always null (so this
    // is safely false there without an extra mode check).
    card: session ? {
      is_raw: session.cert?.facts.is_slab === false, condition: session.card_condition,
      // The read's own PRESENCE flag, off whichever of cert/read the session currently has —
      // a barcode session has neither, so this is safely false there too.
      has_autograph: session.cert?.facts.autograph === true || session.read?.autograph === true,
      autograph_type: session.card_autograph_type ?? null,
    } : undefined,
  }) : []), [m, schema, available, session]);
  const issueMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const i of issues) map[i.field] = i.message;
    for (const i of serverIssues) map[i.field] = i.message; // server wins
    return map;
  }, [issues, serverIssues]);
  const issueCount = Object.keys(issueMap).length;
  // fields that already have a dedicated slot somewhere in the form — anything left over
  // (e.g. a 422 on accepted_new_collections) is surfaced in the fallback list below.
  const placed = useMemo(() => new Set([
    "title", "category", "price_cents", "quantity", "weight_lb", "barcode", "selected_images", "vendor", "product_type", "card_condition", "card_autograph_type",
    ...(m ? m.attributes.map((a) => `attributes.${a.key}`) : []),
  ]), [m]);
  const otherIssues = useMemo(() => Object.entries(issueMap).filter(([field]) => !placed.has(field)), [issueMap, placed]);

  // autosave edits (debounced)
  useEffect(() => {
    if (!form?.dirty) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    const edits = form.edits;
    saveTimer.current = window.setTimeout(() => {
      saveTimer.current = null;
      api.saveListerEdits(sessionId, edits)
        .then(() => { if (mountedRef.current) dispatch({ type: "saved", edits }); })
        .catch((e) => { if (mountedRef.current) setError(String(e)); });
    }, 600);
    return () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); };
  }, [form, sessionId]);

  // flush a still-pending autosave when the page goes away mid-debounce (this effect's
  // cleanup only ever runs on true unmount since sessionId is stable for the component's
  // lifetime — App.tsx keys Review by sessionId so a session change remounts instead).
  useEffect(() => {
    return () => {
      if (saveTimer.current !== null) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
        // never PUT edits for a posted session — create() already settled dirty via its own
        // explicit pre-post save, and the product is already final in Shopify at that point.
        if (formRef.current?.dirty && sessionRef.current?.status !== "posted") {
          void api.saveListerEdits(sessionId, formRef.current.edits).catch(() => {});
        }
      }
    };
  }, [sessionId]);

  async function changeCategory(c: { id: string; full_name: string }): Promise<void> {
    const prevSchema = schema;
    change({ type: "category", id: c.id, full_name: c.full_name });
    setSchema(null);
    try {
      await api.saveListerEdits(sessionId, { category: { id: c.id, full_name: c.full_name, reason: "operator", alternatives: [] }, attributes: [] });
      await api.remapListerSession(sessionId, c.id);
      if (!mountedRef.current) return;
      initializedFor.current = null;
      startPolling();
    } catch (e) {
      if (!mountedRef.current) return;
      // the remap never took: put the previous schema back so the fields don't
      // go blank, forget the stale key, and re-pull the server's own state
      // (still on the original category) rather than leaving our optimistic edit in place.
      setSchema(prevSchema);
      initializedFor.current = null;
      void load();
      handleApiError(e);
    }
  }

  // The condition control's own explicit save — same edits route, same debounce-free pattern
  // changeCategory uses, because the server re-prices the card the moment the condition
  // arrives and the operator should see that price, not a stale one. load() re-pulls the
  // whole session so card_price, price and proposal.price all refresh together.
  async function changeCondition(condition: ListerCardCondition): Promise<void> {
    setConditionSaving(true);
    try {
      await api.saveListerEdits(sessionId, { card_condition: condition });
      await load();
    } catch (e) {
      if (mountedRef.current) handleApiError(e);
    } finally {
      if (mountedRef.current) setConditionSaving(false);
    }
  }

  // The placement's own explicit save. Same edits route, same no-debounce pattern
  // changeCondition uses: the server restamps proposal_json's card.autograph_type on this PUT,
  // so the page must re-pull rather than trust its local copy of the proposal.
  async function changeAutographType(t: ListerAutographType): Promise<void> {
    setAutographSaving(true);
    try {
      await api.saveListerEdits(sessionId, { card_autograph_type: t });
      await load();
    } catch (e) {
      if (mountedRef.current) handleApiError(e);
    } finally {
      if (mountedRef.current) setAutographSaving(false);
    }
  }

  // Restock straight from the review page — the graded card's only restock affordance
  // (CardPanel renders the cert duplicate as amber prose with no control at all). A success
  // re-pulls the session: the server has already written status "restocked", and the ternary
  // below reacts to that on its own rather than this function tracking a local result.
  async function submitRestock(delta: number, mode: "add" | "replace", confirmReplace: boolean): Promise<void> {
    setRestockBusy(true);
    try {
      await api.restockListerSession(sessionId, delta, mode, confirmReplace);
      if (mountedRef.current) await load();
    } catch (e) {
      if (mountedRef.current) handleApiError(e);
    } finally {
      if (mountedRef.current) setRestockBusy(false);
    }
  }

  async function startExistingEnrich(): Promise<void> {
    setExistingAction("enrich");
    setEnrichLoading(true);
    setEnrichResult(null);
    setEnrichFailure(null);
    try {
      const r = await api.getListerExistingDetail(sessionId);
      if (mountedRef.current) setEnrichRows(r.rows);
    } catch (e) {
      if (mountedRef.current) handleApiError(e);
    } finally {
      if (mountedRef.current) setEnrichLoading(false);
    }
  }

  function toggleExistingEnrich(key: string): void {
    setEnrichApply((prev) => {
      const row = enrichRows.find((r) => r.key === key);
      const current = prev[key] ?? row?.auto ?? false;
      return { ...prev, [key]: !current };
    });
  }

  // On success this re-pulls the session too — the server has already written status
  // "enriched", which is what disarms Create (readOnly, below) and switches the right column
  // over to the terminal "Already in Shopify" panel. A 502 leaves the session exactly as it
  // was (still enrichable, still postable, task 9), so a failure never reloads — the rows stay
  // live so the operator can retry.
  async function applyExistingEnrich(): Promise<void> {
    setEnrichBusy(true);
    setEnrichFailure(null);
    try {
      const r = await api.enrichListerSession(sessionId, { apply: enrichApply, images: enrichAppendImages ? "append" : "skip" });
      setEnrichResult(r);
      if (mountedRef.current) await load();
    } catch (e) {
      if (e instanceof ApiError && e.status === 502) {
        const body = e.body as { step: string; applied: string[] };
        if (mountedRef.current) setEnrichFailure({ step: body.step, applied: body.applied });
      } else if (mountedRef.current) {
        handleApiError(e);
      }
    } finally {
      if (mountedRef.current) setEnrichBusy(false);
    }
  }

  async function retry(): Promise<void> {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    try {
      await api.startListerResearch(sessionId, true);
      const s = await load();
      if (!mountedRef.current) return;
      if (TRANSITIONAL.has(s.status)) startPolling();
    } catch (e) {
      if (mountedRef.current) handleApiError(e);
    }
  }

  async function startResearch(): Promise<void> {
    try {
      await api.startListerResearch(sessionId);
      if (!mountedRef.current) return;
      startPolling();
    } catch (e) {
      if (mountedRef.current) handleApiError(e);
    }
  }

  async function create(): Promise<void> {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = null; // otherwise the unmount-flush effect still sees a "pending" timer and re-PUTs
    setPosting(true); setError(null);
    try {
      if (form?.dirty) {
        await api.saveListerEdits(sessionId, form.edits);
        dispatch({ type: "saved", edits: form.edits }); // ack directly — change() would also clear serverIssues, which isn't the point here
      }
      const r = await api.postListerSession(sessionId, confirmed);
      if (!mountedRef.current) return;
      setPosted({ adminUrl: r.adminUrl, warnings: r.warnings });
      // keep session.status in sync so readOnly and the unmount-flush guard see "posted"
      // immediately, without waiting on a reload or poll that may never come.
      setSession((s) => (s ? { ...s, status: "posted", admin_url: r.adminUrl, shopify_product_id: r.productId } : s));
    } catch (e) {
      if (mountedRef.current) handleApiError(e);
    } finally {
      if (mountedRef.current) setPosting(false);
    }
  }

  // DYMO LabelWriter 550 / 30336 label (send-to-lister, task L4): printDocument opens the print
  // window on this click, then fetches the label and prints it — the posted proposal is already
  // frozen (readOnly), so there is no pending edit to flush first, unlike Buy / Trade's own
  // Listing queue print.
  async function printLabel(): Promise<void> {
    setPrintingLabel(true); setError(null);
    try {
      await printDocument(listerSessionLabelPath(sessionId), undefined, "Couldn't print the label. Check the connection and try again.");
    } catch (e) {
      if (mountedRef.current) setError((e as Error).message);
    } finally {
      if (mountedRef.current) setPrintingLabel(false);
    }
  }

  if (!session) return <div className="p-4 font-mono text-zinc-500">{error ?? "loading…"}</div>;
  const needsConfirm = session.verdict?.level === "red" && !confirmed;
  // a forced retry (or any research reset) can null the proposal out from under an
  // already-initialised form; gate the whole form on the *current* proposal, not just
  // on `m` (which stays truthy — derived from the last proposal the reducer saw).
  const proposal = session.proposal;
  // once posted (either loaded that way, or just created in this visit — create() patches
  // session.status locally on success) the proposal is frozen — no more edits should reach
  // the server, only the Shopify link matters now. A transitional status (e.g. "proposing"
  // mid-remap) also means the row's proposal_json is stale and about to be replaced out from
  // under the operator, so the form stays read-only until the fresh proposal lands too.
  // "enriched" and "restocked" join the same guard: both stamp shopify_product_id at an
  // EXISTING product, and the server refuses /post regardless (already_linked, task 9) — this
  // is only the affordance, in the same relationship clientValidate has to validateMerged.
  const readOnly = session.status === "posted" || session.status === "enriched" || session.status === "restocked" || TRANSITIONAL.has(session.status);
  const showExistingBanner = Boolean(session.existing) && !dismissedExisting
    && session.status !== "posted" && session.status !== "enriched" && session.status !== "restocked";

  return (
    <div className="max-w-6xl mx-auto px-4 pt-4 pb-24 md:pb-6 font-mono text-sm md:grid md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] md:gap-6">
      <div className="space-y-4">
        {/* stacked below md: at a phone width the session label (often a full product title) is long
            enough that a single baseline row lets the h1 itself shrink-wrap — "Lister · review" splits
            across three lines, isolating the middle dot. whitespace-nowrap keeps the heading on one
            line; stacking removes the squeeze that caused it in the first place. */}
        <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between">
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <h1 className="text-zinc-100 text-base whitespace-nowrap">Lister · review</h1>
            {/* The operator does not have to wait here. Research and posting carry on server-side
                whatever this page is showing, so the next barcode can go in immediately — which is
                the whole point of scanning a box of stock. Kept beside the heading, above the fold
                at phone width, because that is where a thumb already is after a scan. */}
            <a href={hashFor.lister()} className="shrink-0 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded px-2 py-1">Scan another</a>
            <a href={hashFor.history()} className="shrink-0 text-xs text-zinc-400 underline">history</a>
          </div>
          <span className="text-xs text-zinc-500 truncate">{sessionLabel(session)}</span>
        </div>
        {session.buy_trade && (
          <BuyTradeBadge
            dealNumber={session.buy_trade.deal_number} sku={session.buy_trade.sku}
            grader={session.buy_trade.grader} grade={session.buy_trade.grade} cert={session.buy_trade.cert}
            condition={session.buy_trade.condition}
            mismatch={buyTradeCertMismatch(session.buy_trade, session.cert?.facts ?? null)}
            conditionFlag={buyTradeConditionFlag(session.buy_trade.condition)}
          />
        )}
        {session.verdict && <VerdictBanner verdict={session.verdict} confirmed={confirmed} onConfirm={() => setConfirmed(true)} />}
        {session.read && (
          <>
            <CardPanel
              read={session.read} cert={session.cert} price={session.card_price} existing={session.existing} dup_check={session.dup_check}
              condition={session.card_condition} onCondition={(c) => void changeCondition(c)}
              autograph_type={session.card_autograph_type} onAutographType={(t) => void changeAutographType(t)}
              saving={readOnly || conditionSaving || autographSaving}
              dup_candidates={session.dup_candidates}
            />
            {/* THE POSTING GATE, explained where the control lives: card_condition and
                card_autograph_type are both placed (see `placed` above), so neither duplicates
                into the fallback list below — the operator needs to see WHY Create is disabled
                beside the control that fixes it, not just that it is disabled (ActionBar's
                issue count says only "1 issue"). */}
            {issueMap.card_condition && <p className="text-red-400 text-xs">{issueMap.card_condition}</p>}
            {issueMap.card_autograph_type && <p className="text-red-400 text-xs">{issueMap.card_autograph_type}</p>}
          </>
        )}
        {session.status === "failed" && <div className="border border-red-800 bg-red-900/20 rounded-md p-3 text-red-200">{session.error}<button className="ml-3 underline" onClick={() => void retry()}>retry</button></div>}
        <Section title="Progress">
          <StepProgress steps={session.step_log} status={session.status} mode={session.mode} />
          {TRANSITIONAL.has(session.status) && <LiveLine live={live} />}
        </Section>
        {session.research && m && (
          <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0">
            <Section title="Images">
              <ImagePicker images={session.research.images} selected={m.selected_images} onChange={(urls) => change({ type: "images.set", urls })} />
              {issueMap.selected_images && <p className="text-red-400 text-xs">{issueMap.selected_images}</p>}
              <AddPhotos
                sessionId={session.id}
                empty={session.research.images.length === 0}
                disabled={readOnly}
                onAdded={(s) => { setSession(s); dispatch({ type: "images.available", urls: imagesOf(s) }); }}
              />
            </Section>
          </fieldset>
        )}
        {/* An item session (send-to-lister task L6) is never researched — there is no evidence
            to show, and an empty drawer would read as research that found nothing. */}
        {session.research && session.mode !== "item" && <Section title="Evidence"><EvidenceDrawer research={session.research} /></Section>}
        {session.proposal && session.proposal.notes_for_operator.length > 0 && <Section title="Notes"><ul className="list-disc pl-5 text-xs text-amber-200">{session.proposal.notes_for_operator.map((n, i) => <li key={i}>{n}</li>)}</ul></Section>}
      </div>

      <div className="space-y-4 mt-4 md:mt-0">
        {error && <p className="text-red-400" role="alert">{error}</p>}
        {showExistingBanner && session.existing && (
          <Section title="Already in Shopify">
            <p className="text-zinc-100">{session.existing.title}</p>
            <p className="text-zinc-500 text-xs">{fmtCents(session.existing.price_cents)} · {session.existing.available ?? "?"} in stock · <a className="underline" href={session.existing.admin_url} target="_blank" rel="noopener">open in admin</a></p>
            {existingAction === "enrich" ? (
              enrichLoading ? (
                <p className="text-zinc-500 text-xs">loading the product Shopify actually holds…</p>
              ) : (
                <EnrichPanel
                  rows={enrichRows}
                  apply={enrichApply}
                  onToggle={toggleExistingEnrich}
                  appendImages={enrichAppendImages}
                  onAppendImages={setEnrichAppendImages}
                  busy={enrichBusy}
                  onApply={() => void applyExistingEnrich()}
                  onCancel={() => setExistingAction("none")}
                  result={enrichResult}
                  failure={enrichFailure}
                />
              )
            ) : existingAction === "restock" ? (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <input aria-label="Restock quantity" type="number" inputMode="numeric" min={1} value={restockN}
                    onChange={(e) => { setRestockN(Math.max(1, Number(e.target.value))); setRestockArmed(false); }}
                    className="w-20 bg-zinc-950 border border-zinc-800 rounded px-2 py-3 text-center" />
                  <div className="flex rounded overflow-hidden border border-zinc-800" role="group" aria-label="restock mode">
                    <button type="button" aria-pressed={restockMode === "add"} className={`px-3 py-3 ${restockMode === "add" ? "bg-zinc-700 text-zinc-100" : "bg-zinc-900 text-zinc-400"}`}
                      onClick={() => { setRestockMode("add"); setRestockArmed(false); }}>Add</button>
                    <button type="button" aria-pressed={restockMode === "replace"} className={`px-3 py-3 ${restockMode === "replace" ? "bg-zinc-700 text-zinc-100" : "bg-zinc-900 text-zinc-400"}`}
                      onClick={() => { setRestockMode("replace"); setRestockArmed(false); }}>Replace</button>
                  </div>
                  <button className="bg-zinc-800 hover:bg-zinc-700 px-4 py-3 rounded min-w-[44px]" disabled={restockBusy}
                    onClick={() => {
                      if (restockMode === "add") { void submitRestock(restockN, "add", false); return; }
                      if (!restockArmed) { setRestockArmed(true); return; }
                      void submitRestock(restockN, "replace", true);
                    }}
                  >{restockMode === "add" ? `Add ${restockN}` : restockArmed ? `Replace with ${restockN}` : `Replace with ${restockN}…`}</button>
                </div>
                {restockMode === "replace" && restockArmed && (
                  <p className="text-amber-300 text-xs">
                    {session.existing.available === null
                      ? `This sets the stock level to ${restockN} and discards whatever it is now — this scan cannot see the current count.`
                      : `This discards the current count of ${session.existing.available} and sets the stock level to ${restockN}.`} Press again to replace.
                  </p>
                )}
                <button className="text-zinc-400 underline text-xs" onClick={() => setExistingAction("none")}>Cancel</button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                {/* I1 (send-to-lister review): a Buy / Trade item must become its own product —
                    these two actions would instead stamp stock or fields onto an unrelated
                    existing one, so they're hidden for such a session; the warning above and
                    Create anyway (below) stay. */}
                {!session.buy_trade && <button className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 rounded text-xs" onClick={() => setExistingAction("restock")}>Restock</button>}
                {!session.buy_trade && <button className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 rounded text-xs" onClick={() => void startExistingEnrich()}>Enrich</button>}
                <button className="ml-auto text-zinc-400 underline text-xs" onClick={() => setDismissedExisting(true)}>Create anyway</button>
              </div>
            )}
          </Section>
        )}
        {proposal && m && session.status !== "enriched" && session.status !== "restocked" ? (
          <>
            {TRANSITIONAL.has(session.status) && (
              <p className="text-xs text-zinc-500">Updating proposal… fields unlock when the new proposal arrives.</p>
            )}
            <fieldset disabled={readOnly} className="space-y-4 border-0 p-0 m-0 min-w-0">
              <Section title="Title">
                <Field label="Title"><input aria-label="Title" value={m.title} onChange={(e) => change({ type: "field", key: "title", value: e.target.value })} className={inputCls} /></Field>
                {issueMap.title && <p className="text-red-400 text-xs">{issueMap.title}</p>}
              </Section>
              <Section title="Category">
                <CategoryPicker value={m.category} alternatives={m.category.alternatives} onSearch={(q) => api.searchTaxonomy(q)} onPick={(c) => void changeCategory(c)} />
                {issueMap.category && <p className="text-zinc-500 text-xs">{issueMap.category}</p>}
              </Section>
              <Section title="Category fields"><AttributeFields schema={schema} values={m.attributes} onSet={(key, ids, names) => change({ type: "attribute.set", key, ids, names })} issues={issueMap} /></Section>
              <Section title="Type · Vendor · Tags">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 [&>*]:min-w-0">
                  <Field label="Product type">
                    <input list="types" value={m.product_type} onChange={(e) => change({ type: "field", key: "product_type", value: e.target.value })} className={inputCls} /><datalist id="types">{vocab?.product_types.map((t) => <option key={t} value={t} />)}</datalist>
                    {issueMap.product_type && <p className="text-red-400 text-xs">{issueMap.product_type}</p>}
                  </Field>
                  <Field label="Vendor">
                    <input list="vendors" value={m.vendor} onChange={(e) => change({ type: "field", key: "vendor", value: e.target.value })} className={inputCls} /><datalist id="vendors">{vocab?.vendors.map((t) => <option key={t} value={t} />)}</datalist>
                    {issueMap.vendor && <p className="text-red-400 text-xs">{issueMap.vendor}</p>}
                  </Field>
                </div>
                <TagEditor tags={m.tags} rejected={form.edits.rejected_new_tags ?? []} vocab={vocab?.tags ?? []} onAdd={(v) => change({ type: "tag.add", value: v, isNew: !(vocab?.tags ?? []).includes(v) })} onRemove={(v) => change({ type: "tag.remove", value: v })} onToggleNew={(v) => change({ type: "tag.toggleNew", value: v })} />
              </Section>
              <Section title="Collections"><CollectionsEditor collections={m.collections} accepted={m.accepted_new_collections} onToggle={(t) => change({ type: "collection.toggle", title: t })} /></Section>
              <Section title="Price">
                <PriceCard price={proposal.price} priceCents={m.price_cents} costCents={m.cost_cents}
                  buyTradePriceCents={session.buy_trade?.price_cents ?? null} costLocked={Boolean(session.buy_trade)} researched={session.mode !== "item"}
                  onPrice={(c) => change({ type: "number", key: "price_cents", value: c })} onCost={(c) => change({ type: "number", key: "cost_cents", value: c })} />
                {issueMap.price_cents && <p className="text-red-400 text-xs">{issueMap.price_cents}</p>}
              </Section>
              <Section title="Inventory">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 [&>*]:min-w-0">
                  {session.buy_trade
                    // Buy / Trade's own stated quantity — fixed, never the forced card-mode 1.
                    ? <Field label="Quantity">
                        <input aria-label="Quantity" value={String(m.quantity)} readOnly className={`${inputCls} read-only:opacity-60 read-only:cursor-default`} />
                        <p className="text-zinc-500 text-xs px-1 pt-1">{BUY_TRADE_FIXED_NOTE}</p>
                      </Field>
                    : session.mode === "card"
                    // A single card is always one, and the server forces it regardless of
                    // anything sent here — so this is deliberately not an editable NumberField.
                    // It is still a real <input readOnly>, not a bare <p>: a screen reader
                    // reaches it the same way it reaches every other field on this form (tab
                    // order, "Quantity" as its accessible name, "1" as its value), rather than
                    // relying on linear reading to stumble onto explanatory prose.
                    ? <Field label="Quantity">
                        <input aria-label="Quantity" value="1" readOnly className={`${inputCls} read-only:opacity-60 read-only:cursor-default`} />
                        <p className="text-zinc-500 text-xs px-1 pt-1">a single card is one unit</p>
                      </Field>
                    : <Field label="Quantity"><NumberField kind="integer" value={m.quantity} onCommit={(v) => change({ type: "number", key: "quantity", value: v })} ariaLabel="Quantity" className={inputCls} /></Field>}
                  <Field label="Weight (lb)"><NumberField kind="decimal" value={m.weight_lb} onCommit={(v) => change({ type: "number", key: "weight_lb", value: v })} ariaLabel="Weight" className={inputCls} /></Field>
                  <Field label="SKU">
                    {session.buy_trade
                      ? <><input aria-label="SKU" value={m.sku ?? ""} readOnly className={`${inputCls} read-only:opacity-60 read-only:cursor-default`} /><p className="text-zinc-500 text-xs px-1 pt-1">{BUY_TRADE_FIXED_NOTE}</p></>
                      : <input value={m.sku ?? ""} onChange={(e) => change({ type: "field", key: "sku", value: e.target.value })} className={inputCls} />}
                  </Field>
                </div>
                <Field label="Barcode"><input aria-label="Barcode" inputMode="numeric" value={m.barcode} onChange={(e) => change({ type: "field", key: "barcode", value: e.target.value })} className={inputCls} /></Field>
                {(issueMap.quantity || issueMap.weight_lb || issueMap.barcode) && <p className="text-red-400 text-xs">{[issueMap.quantity, issueMap.weight_lb, issueMap.barcode].filter(Boolean).join(" · ")}</p>}
              </Section>
              <Section title="Description">
                {/* the store's own generated HTML, sanitized before preview; it is never user-supplied from outside the session. */}
                <div className="prose prose-invert prose-sm max-w-none break-words bg-zinc-950 border border-zinc-800 rounded p-3" dangerouslySetInnerHTML={{ __html: sanitizeHtml(m.description_html) }} />
                <textarea aria-label="Description HTML" value={m.description_html} onChange={(e) => change({ type: "field", key: "description_html", value: e.target.value })} rows={6} className={inputCls} />
              </Section>
              <Section title="SEO">
                <Field label="SEO title"><input value={m.seo.title} maxLength={70} onChange={(e) => change({ type: "seo", value: { ...m.seo, title: e.target.value } })} className={inputCls} /></Field>
                <Field label="SEO description"><textarea value={m.seo.description} maxLength={160} rows={2} onChange={(e) => change({ type: "seo", value: { ...m.seo, description: e.target.value } })} className={inputCls} /></Field>
              </Section>
              <Section title="Channels">
                <div className="flex flex-wrap gap-4">{(["online", "pos", "shop", "headless", "facebook"] as const).map((k) => <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={m.channels[k]} onChange={(e) => change({ type: "channel", key: k, value: e.target.checked })} />{{ online: "Online Store", pos: "Point of Sale", shop: "Shop", headless: "Headless", facebook: "Facebook & Instagram" }[k]}</label>)}</div>
                <p className="text-xs text-zinc-500">Unchecking all creates a draft.</p>
              </Section>
            </fieldset>
            {posted?.warnings.length ? <ul className="text-xs text-amber-200 list-disc pl-5">{posted.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul> : null}
            {otherIssues.length > 0 && <ul className="text-red-400 text-xs list-disc pl-5">{otherIssues.map(([field, message]) => <li key={field}>{field}: {message}</li>)}</ul>}
            <ActionBar issues={issueCount} posting={posting} posted={posted} needsConfirm={Boolean(needsConfirm)} dirty={Boolean(form?.dirty)} onCreate={() => void create()} onPrintLabel={() => void printLabel()} printingLabel={printingLabel} />
          </>
        ) : (session.status === "restocked" || session.status === "enriched") && session.existing ? (
          <Section title="Already in Shopify">
            <p className="text-zinc-100">{session.existing.title}</p>
            <p className="text-zinc-500 text-xs">{session.status === "enriched" ? "Enriched — this session is attached to that product and will not create another one." : "Restocked."}</p>
            <a href={session.existing.admin_url} target="_blank" rel="noopener" className="underline text-zinc-300 text-xs">open in Shopify ↗</a>
          </Section>
        ) : session.status === "scanned" ? (
          <div className="space-y-2 text-zinc-500">
            <p>Research not started.</p>
            <button onClick={() => void startResearch()} className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 rounded text-zinc-100">Start research</button>
          </div>
        ) : (
          session.status !== "failed" && <div className="text-zinc-500">{session.mode === "item" ? `${MAP_STAGE_LABEL}…` : "Researching…"} this page updates itself.</div>
        )}
      </div>
    </div>
  );
}
